import {
	ElementRef,
	inject,
	Injectable,
	Renderer2,
	RendererFactory2,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { download, generateCsv, mkConfig } from "export-to-csv";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as QRCode from "qrcode";
import { BehaviorSubject, combineLatest, from, Observable, Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, map, pluck } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { CollectionState, ItemState, ObjectLiteral } from "../../model/custom/Utils";
import { NotificationSnackbarComponent } from "../components/notification-snackbar/notification-snackbar.component";
import {
	WarningModalComponent,
	WarningModalData,
} from "../modals/warning-modal/warning-modal.component";
export interface ProgressStatus {
	message: string;
	percentage: number;
}

@Injectable({
	providedIn: "root",
})
export class UtilsService {
	private matDialog: MatDialog = inject(MatDialog);
	private snackBar: MatSnackBar = inject(MatSnackBar);
	private rendererFactory: RendererFactory2 = inject(RendererFactory2);

	private pdfGenerationProgressSubject = new Subject<ProgressStatus>();
	public pdfGenerationProgress$: Observable<ProgressStatus> =
		this.pdfGenerationProgressSubject.asObservable();
	private pdfGenerationAbortController = new AbortController(); // For cancellation

	get topNav(): HTMLElement {
		return document.querySelector(".navbar");
	}
	get main(): HTMLElement {
		return document.querySelector("main");
	}
	get sideBar(): HTMLElement {
		return document.querySelector(".menu");
	}

	get isFullScreen(): boolean {
		if (this.topNav) {
			return this.topNav.classList.contains(this.cssClasses.navbarHidden);
		}
	}

	cssClasses = {
		navbarHidden: "navbar-hidden",
		fullMenu: "full-menu",
		fullMain: "full-main",
	};

	private renderer: Renderer2;

	constructor() {
		this.renderer = this.rendererFactory.createRenderer(null, null);
	}

	convertObjToQueryString(options, isFullString: boolean = true): string {
		if (!options) {
			return;
		}
		const stringParams = Object.entries(options)
			.filter((e) => e[1] !== undefined)
			.map((kv) => kv.map(encodeURIComponent).join("="))
			.join("&");

		return stringParams ? (isFullString ? `?${stringParams}` : stringParams) : "";
	}

	booleanToDigit<T = ObjectLiteral>(o: ObjectLiteral): T {
		let val = <any>{};
		for (let p in o) {
			if (o.hasOwnProperty(p)) {
				val[p] = typeof o[p] === "boolean" ? +o[p] : o[p];
			}
		}
		return val;
	}

	getCombinedValues<T>(obsObj: {
		[key: string]: BehaviorSubject<any>;
	}): Observable<any> {
		const ent = Object.entries(obsObj);
		const subjects = ent.map(([, value]) => {
			return value.pipe(distinctUntilChanged());
		});
		return combineLatest(subjects).pipe(
			debounceTime(0),
			distinctUntilChanged(),
			map((value) => {
				const params = <any>{};
				ent.map((v, index) => (params[v[0]] = value[index]));
				return params;
			}),
		);
	}

	isLoading<T>(
		source: Observable<ItemState<T> | CollectionState<T>>,
	): Observable<boolean> {
		return source.pipe(
			pluck("isFinishedLoading"),
			map((l) => !l),
		);
	}

	item<T>(source: Observable<ItemState<T>>): Observable<T> {
		return source.pipe(pluck("item"));
	}

	collection<T>(source: Observable<CollectionState<T>>): Observable<T[]> {
		return source.pipe(pluck("items"));
	}

	errors<T>(source: Observable<ItemState<T> | CollectionState<T>>): Observable<any> {
		return source.pipe(pluck("errors"));
	}

	booleanToDigitBoolean(obj: ObjectLiteral) {
		return Object.entries(obj).reduce((acc, entries) => {
			const [prop, val] = entries;
			acc[prop] = typeof val === "boolean" ? +val : val;
			return acc;
		}, {});
	}

	showWarning(
		[message, confirmButtonText, title, cancelButtonText] = [],
		confirmBtnCssClass = "btn-danger",
	): Observable<any> {
		return this.matDialog
			.open<WarningModalComponent, WarningModalData>(WarningModalComponent, {
				data: {
					title,
					message,
					cancelButtonText,
					confirmButtonText,
					confirmBtnCssClass,
				},
			})
			.afterClosed();
	}

	showNote([text, color, icon, duration] = []) {
		const ref = this.snackBar.openFromComponent(NotificationSnackbarComponent, {
			duration: duration || 5000,
		});

		if (text) {
			ref.instance.text = text;
		}

		if (color) {
			ref.instance.color = color;
		}

		if (icon) {
			/**
			 * Material icon
			 */
			ref.instance.icon = icon;
		}
	}

	enterFullScreen() {
		if (this.topNav) {
			this.renderer.addClass(this.topNav, this.cssClasses.navbarHidden);
		}

		if (this.sideBar) {
			this.renderer.addClass(this.sideBar, this.cssClasses.fullMenu);
		}

		if (this.main) {
			this.renderer.addClass(this.main, this.cssClasses.fullMain);
		}
	}

	exitFullScreen() {
		if (this.topNav) {
			this.renderer.removeClass(this.topNav, this.cssClasses.navbarHidden);
		}

		if (this.sideBar) {
			this.renderer.removeClass(this.sideBar, this.cssClasses.fullMenu);
		}

		if (this.main) {
			this.renderer.removeClass(this.main, this.cssClasses.fullMain);
		}
	}

	removeFooterClass() {
		this.renderer.addClass(document.body, "no-footer");
	}

	addFooterClass() {
		this.renderer.removeClass(document.body, "no-footer");
	}

	dateToTimestamp(d: Date): number {
		return Math.floor(d.getTime() / 1000);
	}

	guid(): string {
		return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
			var r = (Math.random() * 16) | 0,
				v = c == "x" ? r : (r & 0x3) | 0x8;
			return v.toString(16);
		});
	}

	moveItems<T>(arr: T[], oldIndex: number, newIndex: number): T[] {
		const copy = Object.assign([], arr);
		if (newIndex >= copy.length) {
			let k = newIndex - copy.length;
			while (k-- + 1) {
				copy.push(undefined);
			}
		}
		copy.splice(newIndex, 0, copy.splice(oldIndex, 1)[0]);
		return [...copy];
	}

	getText(html) {
		const tmp = document.createElement("DIV");
		tmp.innerHTML = html;
		return tmp.textContent || tmp.innerText || "";
	}

	async exportToCSV(data, filename = "reports") {
		const options = mkConfig({
			fieldSeparator: ",",
			quoteStrings: true,
			decimalSeparator: ".",
			showColumnHeaders: true,
			showTitle: true,
			filename,
			useTextFile: false,
			useBom: true,
			useKeysAsHeaders: true,
			title: "",
		});

		const csv = generateCsv(options)(data);
		download(options)(csv);
	}

	exportToPDF(dataToExport, filename) {
		const doc = new jsPDF("p", "px", "a4");
		doc.setFontSize(9);
		doc.text(`Created by ${environment.siteURL}`, 2, 9);
		const head = [Object.keys(dataToExport[0])];
		const data = dataToExport.map((ri) => Object.values(ri));
		autoTable(doc, {
			startY: 14,
			head: head,
			body: data,
			headStyles: {
				cellWidth: "wrap",
				fillColor: "#ff791a",
			},
			bodyStyles: {
				overflow: "linebreak",
				fontSize: 9,
			},
			margin: 2,
		});

		doc.save(`${filename}.pdf`);
	}
	/**
	 * Cancels the ongoing PDF generation.
	 */
	cancelPDFGeneration() {
		this.pdfGenerationAbortController.abort();
		this.pdfGenerationProgressSubject.next({
			message: "PDF generation cancelled by user.",
			percentage: 0,
		});
		// Reset the AbortController for future operations
		this.pdfGenerationAbortController = new AbortController();
	}

	/**
	 * Generates a PDF from an array of HTML elements.
	 * Emits progress updates with messages and percentages.
	 * Can be cancelled using AbortController.
	 * @param pages Array of ElementRefs representing each PDF page
	 * @param fileName Name of the generated PDF file
	 */
	async generatePDF(
		pages: ElementRef[],
		fileName: string = "document.pdf",
	): Promise<void> {
		const pdf = new jsPDF("p", "mm", "a4");
		const totalPages = pages.length;

		try {
			for (let i = 0; i < totalPages; i++) {
				if (this.pdfGenerationAbortController.signal.aborted) {
					throw new Error("PDF generation cancelled");
				}

				const pageNumber = i + 1;
				const percentage = Math.round((pageNumber / totalPages) * 100);
				this.pdfGenerationProgressSubject.next({
					message: `Processing page ${pageNumber} of ${totalPages}`,
					percentage: percentage,
				});

				const page = pages[i].nativeElement;

				// Capture the page content using html2canvas
				const canvas = await html2canvas(page, {
					scale: 2, // Increase scale for better quality
					useCORS: true, // Enable if loading images from external sources
					logging: false, // Disable to reduce console clutter
				});

				if (this.pdfGenerationAbortController.signal.aborted) {
					throw new Error("PDF generation cancelled");
				}

				const imgData = canvas.toDataURL("image/png");

				// Calculate image dimensions to fit A4 size
				const imgProps = pdf.getImageProperties(imgData);
				const pdfWidth = pdf.internal.pageSize.getWidth();
				const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

				if (i > 0) {
					pdf.addPage();
				}

				pdf.addImage(
					imgData,
					"PNG",
					0,
					0,
					pdfWidth,
					pdfHeight,
					undefined,
					"MEDIUM",
				);
				this.pdfGenerationProgressSubject.next({
					message: `Added page ${pageNumber} to PDF`,
					percentage: percentage,
				});
			}

			// Save the generated PDF with blob to handle large files
			const blob = new Blob([pdf.output("blob")], { type: "application/pdf" });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = fileName;
			link.click();
			window.URL.revokeObjectURL(url);

			this.pdfGenerationProgressSubject.next({
				message: "PDF generation completed",
				percentage: 100,
			});
		} catch (error) {
			console.error("Error during PDF generation:", error);
			if (error.message === "PDF generation cancelled") {
				this.pdfGenerationProgressSubject.next({
					message: "PDF generation cancelled",
					percentage: 0,
				});
			} else {
				this.pdfGenerationProgressSubject.next({
					message: "Error occurred during PDF generation",
					percentage: 0,
				});
			}
		}
	}

	getFeaturePlanByDesc<T extends { StripePlanID: string }>(
		plans: T[],
		partOfStripePlanId,
	): T {
		return plans.find((i) => {
			return i.StripePlanID.toLocaleLowerCase().startsWith(
				partOfStripePlanId.toLocaleLowerCase(),
			);
		});
	}

	getFeaturePlansByDesc<T extends { StripePlanID: string }>(
		plans: T[],
		partOfStripePlanId,
	): T[] {
		return plans.filter((i) => {
			return i.StripePlanID.toLocaleLowerCase().startsWith(
				partOfStripePlanId.toLocaleLowerCase(),
			);
		});
	}
	generateQRCode(text: string, size: number): Observable<string> {
		return from(QRCode.toDataURL(text, { width: size, height: size }));
	}
}
